import React, { useState } from 'react';
import Input from '../../components/common/Input/Input';
import Button from '../../components/common/Button/Button';
import { useNotification } from '../../context/NotificationContext';
import { shortenAddress } from '../../utils/formatters';
import { ACTIVE_CONTRACTS } from '../../config/contract';
import { USE_MAINNET } from '../../config/networks';
import { Section, InfoRow, Loading } from './AdminUI';
import { validateLogicCandidate, sameAddr } from './wiring';

const FIX_HINT = {
  config: 'Check the address in src/config/contract.js',
  redeploy: 'Redeploy this contract with the Storage address as constructor argument',
  setLogic: 'Owner must call setLogicContract (button below)'
};

const CheckRow = ({ check }) => (
  <div className={`wiring-check ${check.ok ? 'ok' : 'bad'}`}>
    <div className="wiring-check-head">
      <span className="wiring-check-icon">{check.ok ? '✓' : '✕'}</span>
      <span className="wiring-check-label">{check.label}</span>
    </div>
    {!check.ok && (
      <div className="wiring-check-detail">
        {check.actual !== undefined && (
          <>
            <div>Expected: <span className="mono">{shortenAddress(check.expected, 6)}</span></div>
            <div>On-chain: <span className="mono">{check.actual ? shortenAddress(check.actual, 6) : 'not readable'}</span></div>
          </>
        )}
        {check.actual === undefined && <div className="mono">{check.expected}</div>}
        <div className="wiring-check-hint">{FIX_HINT[check.fix]}</div>
      </div>
    )}
  </div>
);

const AdminWiring = ({ admin, wiring, onRecheck }) => {
  const { provider, runTx, pending, isOwner } = admin;
  const { error: showError } = useNotification();
  const [checking, setChecking] = useState(false);
  const [candidate, setCandidate] = useState('');
  const [validating, setValidating] = useState(false);

  if (!wiring) return <Loading />;

  const recheck = async () => {
    setChecking(true);
    await onRecheck();
    setChecking(false);
  };

  const storageLink = wiring.checks.find((c) => c.key === 'storage-logic');
  const logicLink = wiring.checks.find((c) => c.key === 'logic-storage');
  const canWireConfigured = !storageLink.ok && logicLink.ok;

  const setLogic = async (addr, warn) => {
    setValidating(true);
    const problem = await validateLogicCandidate(provider, addr);
    setValidating(false);
    if (problem) return showError(problem);
    if (!window.confirm(warn)) return;

    const ok = await runTx('set-logic', 'Logic connection', (w) => w.storage.setLogicContract(addr));
    if (ok) {
      setCandidate('');
      recheck();
    }
  };

  const wireConfigured = () => setLogic(
    ACTIVE_CONTRACTS.logic,
    `Point Storage to Logic ${ACTIVE_CONTRACTS.logic}?`
  );

  const upgradeLogic = () => {
    if (sameAddr(candidate, wiring.storageLogic)) return showError('Storage already uses this Logic');
    setLogic(
      candidate,
      `UPGRADE: point Storage to ${candidate}?\n\nThe current Logic stops working immediately. Update "logic" in src/config/contract.js and redeploy the frontend right after.`
    );
  };

  return (
    <>
      <div className={`wiring-summary ${wiring.allOk ? 'ok' : 'bad'}`}>
        <span className="wiring-summary-icon">{wiring.allOk ? '✓' : '!'}</span>
        <div>
          <div className="wiring-summary-title">
            {wiring.allOk ? 'All contracts wired correctly' : 'Wiring problem detected'}
          </div>
          <div className="wiring-summary-sub">
            {wiring.allOk
              ? 'Registrations, investments and withdrawals can go through.'
              : 'User registrations, investments and withdrawals will fail until this is fixed.'}
          </div>
        </div>
      </div>

      <Section title="Checks" subtitle={`${USE_MAINNET ? 'BSC Mainnet' : 'BSC Testnet'} · checked ${new Date(wiring.checkedAt).toLocaleTimeString()}`}>
        {wiring.checks.map((c) => <CheckRow key={c.key} check={c} />)}
        <Button fullWidth variant="secondary" onClick={recheck} loading={checking}>Re-check</Button>
      </Section>

      {!storageLink.ok && (
        <Section title="Connect Logic to Storage" subtitle="Storage only accepts calls from its logic contract. Until this is set every user action reverts with X4.">
          <InfoRow label="Storage" value={shortenAddress(ACTIVE_CONTRACTS.storage, 6)} mono />
          <InfoRow label="Logic (config)" value={shortenAddress(ACTIVE_CONTRACTS.logic, 6)} mono />
          <InfoRow label="Storage points to" value={wiring.storageLogic ? shortenAddress(wiring.storageLogic, 6) : '-'} mono />
          {isOwner ? (
            <Button
              fullWidth
              onClick={wireConfigured}
              loading={validating || pending === 'set-logic'}
              disabled={!canWireConfigured}
            >
              Connect Logic → Storage
            </Button>
          ) : (
            <p className="admin-warning">Only the owner wallet can call setLogicContract. Connect with the owner wallet to fix this.</p>
          )}
          {isOwner && !logicLink.ok && (
            <p className="admin-warning">The configured Logic was not deployed for this Storage, so it can't be connected. Redeploy Logic with the Storage address first.</p>
          )}
        </Section>
      )}

      {isOwner && (
        <Section title="Upgrade Logic" subtitle="Point Storage at a newly deployed Logic. The address is checked first: it must be a contract whose S() is this Storage." danger>
          <InfoRow label="Current Logic" value={wiring.storageLogic ? shortenAddress(wiring.storageLogic, 6) : '-'} mono />
          <Input label="New Logic address" placeholder="0x..." value={candidate} onChange={(e) => setCandidate(e.target.value.trim())} />
          <Button
            fullWidth
            variant="danger"
            onClick={upgradeLogic}
            loading={validating || pending === 'set-logic'}
            disabled={!candidate}
          >
            Validate & Upgrade
          </Button>
        </Section>
      )}

      <Section title="Configured Addresses" subtitle="From src/config/contract.js">
        <InfoRow label="Storage" value={ACTIVE_CONTRACTS.storage} mono />
        <InfoRow label="Logic" value={ACTIVE_CONTRACTS.logic} mono />
        <InfoRow label="Views" value={ACTIVE_CONTRACTS.views} mono />
        <InfoRow label="Token" value={ACTIVE_CONTRACTS.token} mono />
      </Section>
    </>
  );
};

export default AdminWiring;
